import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { setAuthToken, setUser, isAuthenticated } from '../utils/auth';
import './LoginPage.css';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

function LoginPage() {
  const [mode, setMode] = useState('login'); // 'login' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated()) {
      const next = searchParams.get('next') || '/subscriptions';
      navigate(next, { replace: true });
    } else if (searchParams.get('session') === 'expired') {
      setError('Your session has expired. Please log in again.');
    }
  }, [navigate, searchParams]);

  if (isAuthenticated()) {
    return null;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      setAuthToken(res.data.token);
      setUser(res.data.user);
      const next = searchParams.get('next') || '/subscriptions';
      navigate(next, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/request-reset`, { email });
      setSuccess('If an account exists with that email, a reset link has been sent. Check your inbox (and spam folder).');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const switchToForgot = () => {
    setMode('forgot');
    setError('');
    setSuccess('');
    setPassword('');
  };

  const switchToLogin = () => {
    setMode('login');
    setError('');
    setSuccess('');
  };

  return (
    <div className="login-page">
      <header className="login-header">
        <a href="https://openhosst.com" className="login-logo">
          <img src="/logo-dark.png" alt="OpenHosst" />
        </a>
      </header>

      <main className="login-main">
        <div className="login-card">

          {/* Teal accent bar */}
          <div className="login-card-accent" />

          <div className="login-card-body">
            <div className="login-card-header">
              {mode === 'login' ? (
                <>
                  <h1 className="login-title">Welcome back</h1>
                  <p className="login-subtitle">Sign in to manage your hosted applications</p>
                </>
              ) : (
                <>
                  <h1 className="login-title">Reset your password</h1>
                  <p className="login-subtitle">We'll email you a link to reset your password</p>
                </>
              )}
            </div>

            {error && <div className="login-error">{error}</div>}
            {success && <div className="login-success">{success}</div>}

            {mode === 'login' ? (
              <form onSubmit={handleLogin} className="login-form">
                <div className="login-field">
                  <label htmlFor="email">Email address</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    autoFocus
                    required
                  />
                </div>

                <div className="login-field">
                  <div className="login-field-row">
                    <label htmlFor="password">Password</label>
                    <span
                      className="login-forgot"
                      role="button"
                      tabIndex={0}
                      onClick={switchToForgot}
                      onKeyDown={e => e.key === 'Enter' && switchToForgot()}
                    >
                      Forgot password?
                    </span>
                  </div>
                  <div className="login-input-wrap">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      required
                    />
                    <button
                      type="button"
                      className="login-eye-btn"
                      onClick={() => setShowPassword(v => !v)}
                      tabIndex={-1}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                </div>

                <button type="submit" className="login-btn" disabled={loading}>
                  {loading ? 'Signing in…' : 'Sign in'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleForgot} className="login-form">
                <div className="login-field">
                  <label htmlFor="reset-email">Email address</label>
                  <input
                    id="reset-email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    autoFocus
                    required
                  />
                </div>

                <button type="submit" className="login-btn" disabled={loading || !!success}>
                  {loading ? 'Sending…' : 'Send reset link'}
                </button>

                <button type="button" className="login-back-btn" onClick={switchToLogin}>
                  ← Back to sign in
                </button>
              </form>
            )}

            {mode === 'login' && (
              <div className="login-divider">
                <span>Don't have an account?</span>
              </div>
            )}

            {mode === 'login' && (
              <Link to="/signup" className="login-signup-btn">
                Create a free account
              </Link>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default LoginPage;
